import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saveAuth } from '../utils/auth';
import { getAuthBaseUrl, setActiveTenantSlug } from '../utils/tenant';
import { takePostLoginRedirect } from '../utils/postLoginRedirect';
import { dbgV, dbgI, dbgW, dbgE, dbgTimer } from '../utils/debug';
import {
  decideStateAction, isStateIssued, hasAnyIssuedState, consumeState,
  markLoginRetry, hasRecentLoginRetry, clearLoginRetry,
} from '../utils/oidcState';

const COMP = 'AuthCallback';

/**
 * The SDK's /api/auth/callback 400 when the PKCE state is missing, already
 * consumed or expired (cxq_sentinel_client/router.py): "Invalid or expired
 * state parameter — please sign in again". A fresh login fixes it, so the
 * callback restarts login once on this error (issue #1469).
 */
const STATE_INVALID_PREFIX = 'Invalid or expired state parameter';

/** A non-OK /api/auth/callback response, with its status. */
class CallbackExchangeError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'CallbackExchangeError';
    this.status = status;
  }
}

function isStateInvalidError(err: unknown): boolean {
  return err instanceof CallbackExchangeError
    && err.status === 400
    && err.message.startsWith(STATE_INVALID_PREFIX);
}

/**
 * OIDC callback handler.
 *
 * Mounted at /auth/callback. Receives the authorization code from cxq-sentinel-auth,
 * exchanges it for tokens via the backend, and stores the session locally.
 */
export default function AuthCallback() {
  dbgV(COMP, 'component mounted');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');

  // Run-once guard: the authorization code is single-use (the backend pops the
  // PKCE verifier with DELETE...RETURNING), so a StrictMode double-invoke would
  // fire a SECOND /api/auth/callback that fails and could surface a spurious
  // "authentication failed". Process the callback exactly once per mount.
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) {
      dbgV(COMP, 'callback already processed this mount — skipping duplicate (StrictMode)');
      return;
    }
    processedRef.current = true;

    const timer = dbgTimer(COMP);
    dbgV(COMP, 'useEffect: starting callback processing');

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    dbgI(COMP, 'URL params parsed', {
      hasCode: !!code,
      codePrefix: code?.substring(0, 12),
      hasState: !!state,
      errorParam: errorParam ?? null,
      errorDescription: errorDescription ?? null,
    });

    // The early failures below end this flow too: drop its state key so
    // failed attempts don't pile up oidc_state:* keys (issue #1469). Declared
    // here because the state check further down uses the same stores.
    const stores = [sessionStorage, localStorage];
    const dropThisFlowState = () => {
      try {
        consumeState(state, stores, Date.now());
      } catch {
        // a throwing store must not stop the error from showing
      }
    };

    if (errorParam) {
      dbgW(COMP, 'OIDC provider returned an error', { error: errorParam, description: errorDescription });
      dropThisFlowState();
      setError(errorDescription || errorParam);
      return;
    }

    if (!code) {
      dbgE(COMP, 'no authorization code in URL — OIDC redirect may have been malformed');
      dropThisFlowState();
      setError('No authorization code received.');
      return;
    }

    // Validate state via PER-FLOW membership. The client-side check is
    // DEFENSE-IN-DEPTH only — the authoritative CSRF gate is server-side: the
    // backend pops the PKCE verifier keyed by the RETURNED state (single-use
    // DELETE...RETURNING), so an unknown/forged/replayed state is rejected by
    // /api/auth/callback (400) regardless of what the client holds.
    //
    // Each login-init recorded its state under its own key (oidc_state:<state>)
    // in BOTH sessionStorage and localStorage, so concurrent / re-initiated
    // flows can't clobber each other (the #287 silent-bounce root cause). We ask
    // "was this returned state one we issued?" (membership), not "does it equal
    // the single slot we happen to hold?" (equality). See decideStateAction:
    //   - returned state WAS issued              -> PROCEED (happy path).
    //   - not issued AND no live flows exist     -> PROCEED: storage was wiped
    //     across the cross-host redirect (Android/PWA); defer to the backend. A
    //     backend rejection surfaces a REAL error below, not a silent bounce.
    //   - not issued BUT live flows DO exist     -> BOUNCE (genuine anomaly).
    const now = Date.now();
    const stateAction = decideStateAction(state, stores, now);
    dbgV(COMP, 'state validation', {
      receivedState: state?.substring(0, 16),
      issued: isStateIssued(state, stores, now),
      hasLiveFlows: hasAnyIssuedState(stores, now),
      action: stateAction,
    });
    if (stateAction === 'bounce') {
      // Returned state was never issued while we DO hold live flows — a real
      // anomaly (forged / replayed / crossed). Fail closed.
      dbgE(COMP, 'state not issued while live flows exist — failing closed');
      // Consume the offending returned state (defensive) and drop the issuer;
      // sibling live flows keep their keys and can still complete. Pass `now` so
      // expired abandoned-flow keys are swept here too (a retry loop may never
      // reach issueState, the only other sweep site).
      consumeState(state, stores, now);
      sessionStorage.removeItem('oidc_issuer');
      navigate('/login?reason=retry', { replace: true });
      return;
    }
    if (!isStateIssued(state, stores, now)) {
      // Storage wiped (Android cross-host partitioning) — proceed and let the
      // backend be the CSRF authority. NOT a silent bounce; a backend reject
      // shows a real error via the catch below.
      dbgW(COMP, 'returned state not among issued flows (storage wiped?) — deferring to backend state validation');
    }

    const savedIssuer = sessionStorage.getItem('oidc_issuer');
    dbgV(COMP, 'sessionStorage read', { savedIssuer });

    const redirectUri = `${getAuthBaseUrl()}/auth/callback`;
    dbgI(COMP, 'starting code exchange', {
      endpoint: '/api/auth/callback',
      redirectUri,
      codePrefix: code.substring(0, 12) + '...',
    });

    fetch('/api/auth/callback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        state,
        redirect_uri: redirectUri,
      }),
    })
      .then(async (res) => {
        timer('POST /api/auth/callback responded');
        dbgI(COMP, '/api/auth/callback response', { status: res.status, ok: res.ok });
        const text = await res.text();
        dbgV(COMP, 'raw response body', { length: text.length, preview: text.substring(0, 200) });
        let data: Record<string, unknown> = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch (parseErr) {
          dbgE(COMP, 'failed to parse response body as JSON', { parseErr: String(parseErr), text: text.substring(0, 300) });
          throw new Error(`Server returned non-JSON response (${res.status})`);
        }
        dbgV(COMP, 'parsed response fields', {
          hasAccessToken: !!data.access_token,
          hasUser: !!data.user,
          hasRefreshToken: !!data.refresh_token,
          detail: data.detail ?? null,
          error: data.error ?? null,
        });
        if (!res.ok) {
          const msg = typeof data.detail === 'string'
            ? data.detail
            : (data.detail as { message?: string })?.message || `Authentication failed (${res.status})`;
          dbgW(COMP, 'code exchange failed', { status: res.status, message: msg });
          throw new CallbackExchangeError(msg, res.status);
        }
        return data;
      })
      .then((data) => {
        timer('code exchange succeeded');
        dbgI(COMP, 'code exchange succeeded — cleaning up sessionStorage and saving auth');

        // Clean up ONLY this flow's per-flow state key (both stores). Because
        // keys are per-state (oidc_state:<state>), a concurrent login tab's
        // in-flight state is left untouched and can still complete — the shared-
        // localStorage clobber that the old single-key cleanup caused is gone.
        // Passing `now` also sweeps any expired abandoned-flow siblings.
        consumeState(state, stores, now);
        sessionStorage.removeItem('oidc_issuer');
        clearLoginRetry(stores);
        dbgV(COMP, 'per-flow oidc_state consumed', {
          statePrefix: state?.substring(0, 16),
        });

        if (!data.access_token || !data.user) {
          dbgE(COMP, 'response missing required fields', {
            hasAccessToken: !!data.access_token,
            hasUser: !!data.user,
            receivedKeys: Object.keys(data),
          });
          throw new Error('Invalid token response from server');
        }

        saveAuth(
          data.access_token as string,
          data.user as Parameters<typeof saveAuth>[1],
          data.refresh_token as string | undefined,
        );
        timer('auth saved to localStorage');

        // Persist tenant slug at login time so hard-refresh never hits an API
        // call with no X-Tenant header before the async profile fetch completes.
        const user = data.user as { tenant_slug?: string };
        if (user?.tenant_slug) {
          setActiveTenantSlug(user.tenant_slug);
          dbgI(COMP, 'tenant slug persisted at login', { tenant_slug: user.tenant_slug });
        }

        // Return the user to the route they originally requested (saved by the
        // Protected guard), falling back to the dashboard. takePostLoginRedirect
        // sanitises to a same-origin in-app path so a poisoned value can't
        // redirect off-site or loop back into the auth flow.
        const dest = takePostLoginRedirect();

        // Navigate — no setTimeout needed; saveAuth is synchronous localStorage writes.
        // React Router navigate() defers to the next render which reads localStorage correctly.
        dbgI(COMP, 'navigating to post-login destination (replace)', { dest });
        navigate(dest, { replace: true });
        timer('navigate called');
      })
      .catch((err) => {
        dbgE(COMP, 'error during callback processing', {
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        });
        // This flow is over whatever went wrong: the code and its state are
        // single-use, and this mount never retries them. Drop the flow's
        // oidc_state:<state> key (both stores) so failed attempts don't pile
        // up keys (issue #1469). Idempotent after the success-path consume.
        dropThisFlowState();
        try {
          sessionStorage.removeItem('oidc_issuer');
        } catch {
          // ignore
        }

        // The SDK's state-invalid 400 (state missing / consumed / expired):
        // restart login ONCE. The marker (dual-stored, see oidcState) makes a
        // second state-invalid 400 within the window show the error instead,
        // so this can never loop. Every other error shows the card.
        if (isStateInvalidError(err)) {
          const t = Date.now();
          if (!hasRecentLoginRetry(stores, t)) {
            markLoginRetry(stores, t);
            // Restart only if the marker reads back from LOCALSTORAGE, the
            // store that survives the IdP redirect (sessionStorage can be
            // dropped there, Android/PWA). A marker held only in
            // sessionStorage could vanish and the next state-invalid 400
            // would restart again. Fail closed: show the error instead.
            if (hasRecentLoginRetry([localStorage], t)) {
              dbgW(COMP, 'state-invalid 400 — restarting login once');
              navigate('/login?reason=retry', { replace: true });
              return;
            }
            dbgW(COMP, 'state-invalid 400 — restart marker not writable, showing the error');
          } else {
            dbgW(COMP, 'state-invalid 400 again after a restart — showing the error');
          }
        }
        clearLoginRetry(stores);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-0.5px', marginBottom: 4 }}>
              ResponseKit
            </div>
          </div>
          <div className="card" style={{ padding: '32px 28px', textAlign: 'center' }}>
            <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>Sign-in failed</p>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              {error}
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/login', { replace: true })}
              style={{ padding: '12px 24px', fontWeight: 600 }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state while exchanging code
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-0.5px', marginBottom: 16 }}>
          ResponseKit
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          Completing sign-in...
        </p>
      </div>
    </div>
  );
}
